import { APIResponse, Post, Alert, Metrics, Stats, UserNetwork } from '../types';

// Determine API base URL based on environment
const getApiBase = () => {
  // Always use the Pages Function endpoint
  // This provides secure service binding to backend
  return '/api';
};

const API_BASE = getApiBase();
// No more API key needed with service bindings!
const API_KEY = import.meta.env.MODE === 'development' ? 'dev-verification-key' : '';

// Debug logging (only in development)
if (import.meta.env.MODE === 'development') {
  console.log('API Configuration:', {
    API_BASE,
    API_KEY: API_KEY ? 'Set' : 'Not set',
    ENV: import.meta.env.MODE
  });
}

class APIClient {
  private async request<T>(endpoint: string, options: RequestInit & { timeout?: number } = {}): Promise<T> {
    // Add cache-busting for GET requests
    const cacheBustedEndpoint = (options.method === 'POST' || options.method === 'PUT' || options.method === 'DELETE')
      ? endpoint
      : endpoint.includes('?')
        ? `${endpoint}&_t=${Date.now()}`
        : `${endpoint}?_t=${Date.now()}`;

    const url = `${API_BASE}${cacheBustedEndpoint}`;
    if (import.meta.env.MODE === 'development') {
      console.log(`API Request: ${options.method || 'GET'} ${url}`);
    }

    const { timeout = 15000, ...fetchOptions } = options;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          ...(API_KEY ? { 'X-API-Key': API_KEY } : {}),
          ...options.headers,
        },
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const result: APIResponse<T> = await response.json();
      if (import.meta.env.MODE === 'development') {
        console.log('API Response:', { success: result.success, hasData: !!result.data });
      }

      if (!result.success) {
        console.error('API Error:', result.error);
        throw new Error(result.error || 'API request failed');
      }

      return result.data as T;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error(`API Request timed out after ${timeout}ms`);
      }
      throw error;
    } finally {
      clearTimeout(id);
    }
  }

  async getPosts(params: {
    page?: number;
    limit?: number;
    threat_level?: string;
    username?: string;
    from_date?: string;
    to_date?: string;
    analyzed?: string;
    order_by?: string;
    search?: string;
    source_feed?: string;
    feed_type?: string;
    threat_type?: string;
  } = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    return this.request<{
      posts: Post[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>(`/v1/posts?${searchParams}`);
  }

  async getPost(id: string) {
    return this.request<{
      post: Post;
      analysis?: any;
      cached_analysis?: any;
    }>(`/v1/posts/${id}`);
  }

  async getPostDetails(id: string) {
    return this.request<any>(`/v1/posts/${id}`);
  }

  async getPostDetailsByAnalysisId(analysisId: string) {
    return this.request<any>(`/v1/analysis/${analysisId}/post-details`);
  }

  async getAnalysisSteps(id: string) {
    return this.request<any>(`/v1/posts/${id}/analysis-steps`);
  }

  async getAnalysisStepsByAnalysisId(analysisId: string) {
    return this.request<any>(`/v1/analysis/${analysisId}/analysis-steps`);
  }

  async searchPosts(query: string, limit = 20) {
    return this.request<{
      query: string;
      posts: Post[];
      count: number;
    }>(`/v1/search?q=${encodeURIComponent(query)}&limit=${limit}`);
  }

  async getAlerts(params: {
    page?: number;
    limit?: number;
    severity?: string;
    acknowledged?: boolean;
    from_date?: string;
    to_date?: string;
    post_id?: string;
  } = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    return this.request<{
      alerts: Alert[];
      pagination: { page: number; limit: number; total: number; pages: number };
      total: number;
    }>(`/v1/alerts?${searchParams}`);
  }

  async acknowledgeAlert(id: string) {
    return this.request<{ message: string }>(`/v1/alerts/${id}/acknowledge`, {
      method: 'PUT',
    });
  }

  async getMetrics(params: {
    from_date?: string;
    to_date?: string;
  } = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    return this.request<Metrics>(`/v1/metrics${queryString ? `?${queryString}` : ''}`);
  }

  async getStats(range = '24h', params: {
    from_date?: string;
    to_date?: string;
  } = {}) {
    const searchParams = new URLSearchParams();
    searchParams.append('range', range);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    return this.request<Stats>(`/v1/stats?${searchParams}`);
  }

  async getUserNetwork(username: string, depth = 1) {
    return this.request<UserNetwork>(`/v1/users/${username}/network?depth=${depth}`);
  }

  async triggerIngestion(feedName: string) {
    return this.request<{ feedName: string; processedCount: number; message: string }>(
      `/v1/ingest/${feedName}`,
      { method: 'POST' }
    );
  }

  async getRSSAppFeeds() {
    return this.request<{
      feeds: Array<{
        id: string;
        title: string;
        description: string;
        link: string;
        items_count: number;
        created_at: string;
        updated_at: string;
        last_post_date?: string | null;
        is_active?: boolean;
      }>;
      count: number;
      message?: string;
      error?: string;
      configurationRequired?: boolean;
      fromCache?: boolean;
      fetchTime?: number;
    }>('/v1/rss-app/feeds');
  }

  async deleteRSSAppFeeds(feedIds: string[]) {
    return this.request<{
      deleted: number;
      message: string;
    }>('/v1/rss-app/feeds', {
      method: 'DELETE',
      body: JSON.stringify({ feedIds })
    });
  }


  async getCronStatus() {
    return this.request<{
      cronJobs: Array<{
        id: string;
        schedule: string;
        description: string;
        key: string;
        lastRun: string | null;
        status: string;
      }>;
    }>('/v1/cron-status');
  }

  async triggerRSSAppIngestion(feedId?: string) {
    return this.request<{ feedId?: string; processedCount?: number; message: string }>(
      '/v1/rss-app/ingest',
      {
        method: 'POST',
        body: JSON.stringify({ feedId })
      }
    );
  }

  // Settings Management
  async getSettings() {
    return this.request<{
      aiAnalysisMode: 'existing' | 'advanced';
      ocrEnabled: boolean;
      multiModalAnalysis: boolean;
      confidenceThreshold: number;
      alertThreatLevel: 'medium' | 'high' | 'critical';
      analysisThreatLevel: 'low' | 'medium' | 'high' | 'critical';
      analysisDepth: number;
      aiProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare';
      aiModel: string;
      aiFallbackProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'mistral' | 'deepseek' | 'none';
      aiFallbackModel: string;
      relevanceAiProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'mistral' | 'deepseek';
      relevanceAiModel: string;
      relevanceFallbackProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'none';
      relevanceFallbackModel: string;
      enhancedAiEnabled: boolean;
      enhancedAiTrigger: 'always' | 'medium_threats_and_above' | 'high_threats' | 'critical_only' | 'never';
      enhancedAiProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare';
      enhancedAiModel: string;
      enhancedAiFallbackProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'none';
      enhancedAiFallbackModel: string;
      aiAnalysisConcurrency: number;
      r2RetentionDays: number;
    }>('/v1/settings');
  }

  async saveSettings(settings: {
    aiAnalysisMode: 'existing' | 'advanced';
    ocrEnabled: boolean;
    multiModalAnalysis: boolean;
    confidenceThreshold: number;
    alertThreatLevel: 'medium' | 'high' | 'critical';
    analysisThreatLevel: 'low' | 'medium' | 'high' | 'critical';
    analysisDepth: number;
    aiProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare';
    aiModel: string;
    aiFallbackProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'mistral' | 'deepseek' | 'none';
    aiFallbackModel: string;
    relevanceAiProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'mistral' | 'deepseek';
    relevanceAiModel: string;
    relevanceFallbackProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'none';
    relevanceFallbackModel: string;
    enhancedAiEnabled: boolean;
    enhancedAiTrigger: 'always' | 'medium_threats_and_above' | 'high_threats' | 'critical_only' | 'never';
    enhancedAiProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare';
    enhancedAiModel: string;
    enhancedAiFallbackProvider: 'anthropic' | 'openai' | 'grok' | 'gemini' | 'cloudflare' | 'none';
    enhancedAiFallbackModel: string;
    aiAnalysisConcurrency: number;
    r2RetentionDays: number;
    postsRetentionDays: number;
    criticalRetentionDays: number;
    logsRetentionDays: number;
  }) {
    return this.request<{ message: string }>('/v1/settings', {
      method: 'POST',
      body: JSON.stringify(settings)
    });
  }

  async manualFlush(forceFlushAll: boolean = false) {
    return this.request<{
      message: string;
      flushed_count: number;
      levels_flushed?: string[];
      stats_before?: any;
      total_remaining?: number;
      current_threshold?: string;
      stats?: any;
    }>('/v1/settings/manual-flush', {
      method: 'POST',
      body: JSON.stringify({ forceFlushAll })
    });
  }

  async getRelevanceAnalysis(postId: string) {
    return this.request<{
      id: string;
      postId: string;
      provider: string;
      model: string;
      rawScore: number;
      normalizedScore: number;
      analysisText: string;
      reasoning: string;
      keywordsFound: string[];
      keywordsMatched: string[];
      threatIndicators: string[];
      confidenceFactors: {
        sourceCredibility: number;
        contentSpecificity: number;
        technicalDepth: number;
        timelineRelevance: number;
      };
      fallbackReason?: string;
      processingTime: number;
      createdAt: string;
    }>(`/v1/posts/${postId}/relevance-analysis`);
  }

  async getProviderStatus() {
    return this.request<{
      providers: Record<string, {
        configured: boolean;
        available_models: string[];
        default_model: string;
        last_24h_usage: {
          total_analyses: number;
          models_used: Array<{
            model: string;
            count: number;
            last_used: string;
            success_rate: number;
          }>;
          error_count: number;
          last_error: string | null;
        };
        status: 'operational' | 'degraded' | 'not_configured';
        is_fallback: boolean;
      }>;
      current_settings: {
        primary_provider: string;
        primary_model: string;
        fallback_enabled: boolean;
        fallback_provider: string;
        fallback_model: string;
      };
      system_health: {
        total_providers_configured: number;
        total_analyses_24h: number;
        fallback_usage_24h: number;
        primary_provider_healthy: boolean;
      };
    }>('/v1/providers/status');
  }

  async triggerProviderHealthCheck(provider?: string, model?: string) {
    return this.request<Record<string, any>>('/v1/providers/health-check', {
      method: 'POST',
      body: JSON.stringify({
        provider,
        model,
        forceCheck: true
      })
    });
  }

  async getRssAppFeeds(): Promise<{
    feeds: Array<{
      id: string;
      source_feed: string;
      post_count: number;
      name: string;
      url: string;
      description: string;
      last_synced: string | null;
    }>;
    total: number;
  }> {
    return this.request('/v1/feeds/rss-app');
  }

  async getRssFeeds(): Promise<{
    feeds: Array<{
      id: string;
      source_feed: string;
      post_count: number;
      name: string;
      url: string;
      description: string;
      last_synced: string | null;
    }>;
    total: number;
  }> {
    return this.request('/v1/feeds/rss');
  }

  async getThreatTypes(): Promise<{
    threat_types: Array<{
      name: string;
      display_name: string;
      count: number;
    }>;
    total: number;
  }> {
    return this.request('/v1/threat-types');
  }

  async getMonitoringSources(): Promise<{
    sources: Array<{
      source: string;
      description: string;
      feed_type: string;
      status: 'active' | 'inactive' | 'error';
      last_ingestion: string | null;
      last_success: string | null;
      total_posts: number;
      posts_24h: number;
      average_daily: number;
      success_rate: number;
    }>;
    summary: {
      total_sources: number;
      active_sources: number;
      total_posts_24h: number;
      average_success_rate: number;
    };
  }> {
    return this.request('/v1/monitoring/sources');
  }

  async getQueueStatus(): Promise<{
    queue_health: {
      total_stuck_posts: number;
      high_relevance_stuck: number;
      stuck_last_hour: number;
      stuck_last_24h: number;
      avg_relevance_score: number;
      oldest_stuck: string | null;
      newest_stuck: string | null;
    };
    recent_activity: {
      posts_created_last_hour: number;
      posts_processed_last_hour: number;
      posts_analyzed_last_hour: number;
      processing_rate: number;
      analysis_rate: number;
    };
    sample_stuck_posts: Array<{
      id: string;
      title: string;
      created_at: string;
      relevance_score: number;
      source_feed: string;
    }>;
  }> {
    return this.request('/v1/debug/queue-status');
  }

  async processQueueManually(): Promise<{
    message: string;
    processed: number;
    total_found: number;
    posts: Array<{
      id: string;
      title: string;
      relevance_score: number;
      status: string;
      error?: string;
    }>;
  }> {
    return this.request('/v1/queue/process', {
      method: 'POST'
    });
  }

  // Generic HTTP methods for extensions
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  async post<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async put<T = any>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  async getEnhancedRelevanceMetrics(): Promise<{
    totalAnalyzed: number;
    educationalFiltered: number;
    operationalBoosted: number;
    averageScore: number;
    contentTypeDistribution: Array<{
      type: string;
      count: number;
      percentage: number;
      color: string;
    }>;
    scoringComponents: Array<{
      component: string;
      averageScore: number;
      weight: number;
    }>;
    recentAnalysis: Array<{
      timestamp: string;
      content: string;
      contentType: string;
      finalScore: number;
      components: {
        contentTypeScore: number;
        temporalRelevanceScore: number;
        iocActionabilityScore: number;
        mitreConfidenceScore: number;
        attributionScore: number;
      };
      provider: string;
      processingTime: number;
    }>;
    providerStats: Array<{
      provider: string;
      requests: number;
      averageTime: number;
      successRate: number;
    }>;
    qualityMetrics: {
      averageConfidence: number;
      reprocessingRate: number;
      qualityGradeDistribution: Array<{
        grade: string;
        count: number;
      }>;
    };
    cachingStats: {
      hitRate: number;
      totalRequests: number;
      cacheMisses: number;
      averageResponseTime: number;
    };
  }> {
    return this.request('/v1/enhanced-relevance/metrics');
  }

  async getDepthAnalysis(postId: string, depthLevel: number): Promise<{
    analyses: Array<{
      id: string;
      post_id: string;
      url: string;
      depth_level: number;
      parent_url?: string;
      crawled_at: string;
      status: 'pending' | 'crawling' | 'completed' | 'failed' | 'skipped';
      error_message?: string;
      title?: string;
      content?: string;
      description?: string;
      extracted_urls?: string[];
      extracted_iocs?: any[];
      images_count: number;
      links_count: number;
      threat_score: number;
      threat_level?: string;
      confidence_score: number;
      threat_categories?: string[];
      key_entities?: string[];
      response_time?: number;
      content_length?: number;
      status_code?: number;
    }>;
    total: number;
  }> {
    return this.request(`/v1/posts/${postId}/depth-analysis/${depthLevel}`);
  }
}

export const api = new APIClient();